import { useEffect, useRef, useState, useMemo } from 'react';
import { Icon } from './Icon';

/**
 * SearchableSelect
 *
 * A lightweight typeahead dropdown — looks like a normal select but
 * supports filtering. Used wherever there are too many options for
 * a plain <select> to be ergonomic (template pickers, member pickers, etc.)
 *
 * Props:
 *   value         – currently-selected option's value, or null
 *   onChange(v)   – called with the new value (or null for cleared)
 *   options       – [{ value, label, hint?, group? }, ...]
 *   placeholder   – text to show when empty
 *   emptyOption   – optional { value, label } shown at top (e.g. "— Unclassified —")
 *   disabled      – boolean
 *   width         – CSS width (defaults '100%')
 */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  emptyOption = null,
  disabled = false,
  width = '100%',
}) {
  const [open, setOpen]         = useState(false);
  const [query, setQuery]       = useState('');
  const [hoverIndex, setHoverIndex] = useState(0);
  const ref      = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const visibleOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const allOptions = emptyOption ? [emptyOption, ...options] : options;
    if (!q) return allOptions;
    return allOptions.filter(o =>
      (o.label || '').toLowerCase().includes(q) ||
      (o.hint || '').toLowerCase().includes(q)
    );
  }, [options, query, emptyOption]);

  const selectedLabel = (() => {
    if (value === null || value === undefined || value === '') {
      return emptyOption?.label || placeholder;
    }
    const all = emptyOption ? [emptyOption, ...options] : options;
    const found = all.find(o => o.value === value);
    return found?.label || placeholder;
  })();

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHoverIndex(i => Math.min(visibleOptions.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHoverIndex(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = visibleOptions[hoverIndex];
      if (opt) {
        onChange(opt.value);
        setOpen(false);
        setQuery('');
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative', width }}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        style={{
          boxSizing: 'border-box',
          width: '100%', height: 36, padding: '0 12px',
          fontSize: 13, fontFamily: 'inherit',
          color: 'var(--text)',
          background: 'var(--surface)',
          border: `1px solid ${open ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: 'var(--r-md)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 6, textAlign: 'left',
          opacity: disabled ? 0.6 : 1,
          boxShadow: open ? 'var(--shadow-glow)' : 'none',
          transition: 'border-color 0.12s, box-shadow 0.12s',
        }}
      >
        <span style={{
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: !value && !emptyOption ? 'var(--text-muted)' : 'var(--text)',
        }}>
          {selectedLabel}
        </span>
        <Icon.chevD size={12} stroke="var(--text-muted)" />
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 250,
          maxHeight: 320, display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Search input */}
          <div style={{
            padding: 8, borderBottom: '1px solid var(--border)',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', left: 18, top: 0, bottom: 0,
              display: 'flex', alignItems: 'center', pointerEvents: 'none',
              color: 'var(--text-muted)',
            }}>
              <Icon.search size={13} />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHoverIndex(0); }}
              onKeyDown={onKeyDown}
              placeholder="Search…"
              className="mi-focusable"
              style={{
                boxSizing: 'border-box',
                // display:block — see Input primitive. Avoids the inline-block line-box
                // gap that otherwise pushes the search icon below the field's center.
                display: 'block',
                width: '100%', height: 32, padding: '0 10px 0 32px',
                fontSize: 13, fontFamily: 'inherit', color: 'var(--text)',
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)',
              }}
            />
          </div>

          {/* Option list */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {visibleOptions.length === 0 ? (
              <div style={{
                padding: 16, textAlign: 'center',
                fontSize: 12, color: 'var(--text-3)',
              }}>
                No matches.
              </div>
            ) : (
              visibleOptions.map((opt, idx) => {
                const isSelected = opt.value === value;
                const isHovered  = idx === hoverIndex;
                return (
                  <button
                    key={`${opt.value}-${idx}`}
                    type="button"
                    onMouseEnter={() => setHoverIndex(idx)}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                      setQuery('');
                    }}
                    style={{
                      width: '100%', padding: '8px 12px',
                      textAlign: 'left', border: 'none',
                      background: isHovered ? 'var(--surface-2)' : 'transparent',
                      cursor: 'pointer', fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', gap: 8,
                      fontSize: 13, color: 'var(--text)',
                    }}
                  >
                    <span style={{
                      width: 14, display: 'inline-flex', alignItems: 'center',
                      color: isSelected ? 'var(--accent)' : 'transparent',
                      flexShrink: 0,
                    }}>
                      <Icon.check size={12} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: isSelected ? 600 : 500,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {opt.label}
                      </div>
                      {opt.hint && (
                        <div style={{
                          fontSize: 11, color: 'var(--text-3)', marginTop: 1,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {opt.hint}
                        </div>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
